import {
  createSlice,
  createAsyncThunk,
  createSelector,
} from '@reduxjs/toolkit';
import { dashboardAPI as apiConfig } from '../../../axiosConfig';
import { getEncodedURL } from '../../../utils/utils';

const URL = 'payment-options-proxy?endpoint=paymentdashboard';

export const getMethodMatrix = createAsyncThunk(
  'getMethodMatrix',
  async (id, { rejectWithValue }) => {
    try {
      const url = getEncodedURL(`${URL}/methodmatrix`, { lobIds: id });
      const { data, status } = await apiConfig.get(url);

      if (status !== 200 || data?.status !== 'SUCCESS') {
        return rejectWithValue(data);
      }

      const { methodMatrix } = data;

      return { methodMatrix, id };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
  {
    condition: (id, { getState }) => {
      const {
        paymentOptions: {
          methodMatrix: { methodMatrixDataLobIds },
        },
      } = getState();

      // Check if data already exists for lob id
      if (methodMatrixDataLobIds.includes(id)) return false;

      return true;
    },
  },
);

const SLICE_NAME = 'paymentOptionsMethodMatrix';

const initialState = {
  methodMatrixData: [],
  methodMatrixDataLobIds: [],
  methodMatrixDataStatus: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed',

  filteredLOB: [],
  filteredPaymentMethod: [],
  filteredPlatform: [],
  filteredCurrency: [],
  filteredGateway: [],
  filteredStatus: [],
};

const paymentOptionsMethodMatrixSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    resetFilters(state) {
      state.filteredLOB = [];
      state.filteredPaymentMethod = [];
      state.filteredPlatform = [];
      state.filteredCurrency = [];
      state.filteredGateway = [];
      state.filteredStatus = [];
    },
    setFilters(state, { payload }) {
      const {
        filteredLOB = [],
        filteredPaymentMethod = [],
        filteredPlatform = [],
        filteredCurrency = [],
        filteredGateway = [],
        filteredStatus = [],
      } = payload;

      state.filteredLOB = filteredLOB.map(({ lobId }) => lobId);
      state.filteredPaymentMethod = filteredPaymentMethod.map(
        ({ methodId }) => methodId,
      );
      state.filteredPlatform = filteredPlatform.map(({ id }) => id);
      state.filteredCurrency = filteredCurrency.map(({ id }) => id);
      state.filteredGateway = filteredGateway.map(({ id }) => id);
      state.filteredStatus = filteredStatus.map(({ id }) => id);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getMethodMatrix.pending, (state) => {
        state.methodMatrixDataStatus = 'loading';
      })
      .addCase(getMethodMatrix.rejected, (state) => {
        state.methodMatrixDataStatus = 'failed';
      })
      .addCase(getMethodMatrix.fulfilled, (state, { payload }) => {
        const { methodMatrix, id } = payload;
        if (!state.methodMatrixDataLobIds.includes(id)) {
          state.methodMatrixData.push(...methodMatrix);
          state.methodMatrixDataLobIds.push(id);
        }
        state.methodMatrixDataStatus = 'succeeded';
      });
  },
});

export const paymentOptionsMethodMatrixActions =
  paymentOptionsMethodMatrixSlice.actions;

export const selectMethodMatrixData = (state) =>
  state.paymentOptions.methodMatrix.methodMatrixData;

export const selectIsLoadingMethodMatrixData = (state) =>
  state.paymentOptions.methodMatrix.methodMatrixDataStatus === 'loading';

export const selectFilteredLOB = (state) =>
  state.paymentOptions.methodMatrix.filteredLOB || [];

export const selectFilteredPaymentMethod = (state) =>
  state.paymentOptions.methodMatrix.filteredPaymentMethod;

export const selectFilteredPlatform = (state) =>
  state.paymentOptions.methodMatrix.filteredPlatform;

export const selectFilteredCurrency = (state) =>
  state.paymentOptions.methodMatrix.filteredCurrency;

export const selectFilteredGateway = (state) =>
  state.paymentOptions.methodMatrix.filteredGateway;

export const selectFilteredStatus = (state) =>
  state.paymentOptions.methodMatrix.filteredStatus;

export const selectHasFilters = createSelector(
  selectFilteredLOB,
  selectFilteredPaymentMethod,
  selectFilteredPlatform,
  selectFilteredCurrency,
  selectFilteredGateway,
  selectFilteredStatus,
  (
    filteredLOB,
    filteredPaymentMethod,
    filteredPlatform,
    filteredCurrency,
    filteredGateway,
    filteredStatus,
  ) => {
    if (
      !!filteredLOB.length ||
      !!filteredPaymentMethod.length ||
      !!filteredPlatform.length ||
      !!filteredCurrency.length ||
      !!filteredGateway.length ||
      !!filteredStatus.length
    ) {
      return true;
    }

    return false;
  },
);

export const selectGroupedMethodMatrixData = createSelector(
  selectMethodMatrixData,
  selectFilteredPaymentMethod,
  selectFilteredPlatform,
  selectFilteredCurrency,
  selectFilteredGateway,
  selectFilteredStatus,
  (
    methodMatrixData,
    filteredPaymentMethod,
    filteredPlatform,
    filteredCurrency,
    filteredGateway,
    filteredStatus,
  ) => {
    const processedMethodMatrixData = methodMatrixData.reduce((acc, item) => {
      const {
        platformCode,
        methodStatus,
        lobId,
        platformId,
        methodId,
        gatewayId,
        currencyId,
      } = item;

      const isValidPaymentMethod =
        !filteredPaymentMethod.length ||
        filteredPaymentMethod.includes(methodId);

      const isValidPlatform =
        !filteredPlatform.length || filteredPlatform.includes(platformId);

      const isValidCurrency =
        !filteredCurrency.length || filteredCurrency.includes(currencyId);

      const isValidGateway =
        !filteredGateway.length || filteredGateway.includes(gatewayId);

      const isValidStatus =
        !filteredStatus.length || filteredStatus.includes(methodStatus);

      if (
        !isValidPaymentMethod ||
        !isValidPlatform ||
        !isValidStatus ||
        !isValidGateway ||
        !isValidCurrency
      )
        return acc;

      if (!acc[lobId]) acc[lobId] = {};
      if (!acc[lobId][platformCode]) acc[lobId][platformCode] = [];

      acc[lobId][platformCode].push(item);

      return acc;
    }, {});

    return processedMethodMatrixData;
  },
);

export default paymentOptionsMethodMatrixSlice.reducer;
